import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getPublicDealsRetailer, type PublicDealsRetailer } from "../../config/publicDeals";
import { browsePublicProducts } from "../../services/ProductsService";
import type { PublicPreviewDeal } from "../../services/PublicPreviewService";
import { type PublicFreeDeal } from "../../services/PublicFreeDealService";
import { publicFreeDealQuery } from "../../services/publicClearanceQueries";
import ClearanceSpotlight from "./ClearanceSpotlight";
import { getPublicMemberHauls, type PublicMemberHaul } from "../../services/CommunitySuccessService";
import type { ProductListItem } from "../../types/Product";
import { getProductImageUrl } from "../../utils/imageUtils";
import { getGuestDealDiscount, getGuestDealPrice } from "../guest-deals/GuestDealsUi.utils";
import {
  BASIC_INTRO_LABEL,
  BASIC_PRICE_LABEL,
  PRICING_PLANS,
  formatPlanPrice,
} from "../../utils/basicPlanOffer";
import { isApparel, prioritizeNonApparel, rotateDeals } from "./homeFeedLogic";
import { HF, cleanName, retailerLabel } from "./homeFeedUi";
import { track } from "../../utils/analytics";
import { openLogfrogChat } from "../../utils/logfrog";
import { AppShowcase, BRAND, HIGHLIGHT, HIGHLIGHT_INK, LandingNav, LockGlyph, SalePrice } from "./HomePromos";
import ProductTileImage from "./ProductTileImage";

// Colours that never change with the theme (gold marks, brand buttons, badges on light photo tiles).
const INK = "#170B26";
const PURPLE = BRAND;
const GOLD = "#FFD24A";
/** The home page's display face (loaded in index.html), over the app's system stack. */
const LANDING_FONT = `Outfit, ${HF.font}`;
/** Grey square behind product photos; stays light in dark mode (photos have white backgrounds). */
const CARD_TILE = "var(--hf-card-tile)";
const DEAL_GREEN = "#0A7A3B";
// Theme-aware (CSS variables, see src/index.css).
const SECTION = HF.surface2;
const TILE = HF.tile;
const SKELETON = HF.skeleton;
const MUTED = HF.text3;
const PANEL_BORDER = HF.border;
const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Online stores featured on the home page: one top deal each, plus a "shop by store" tile. */
const ONLINE_SLUGS = ["amazon", "target", "gamestop", "best-buy", "walmart", "kohls", "nike"];
const ONLINE_STORES = ONLINE_SLUGS.map((slug) => getPublicDealsRetailer(slug)).filter(
  (retailer): retailer is PublicDealsRetailer => Boolean(retailer),
);
/** Stores that get their own "Top from …" row further down; a superset of ONLINE_SLUGS so one fetch feeds both. */
const ROW_SLUGS = ["walmart", "amazon", "target", "best-buy", "gamestop", "costco", "kohls", "newegg", "menards", "woot", "ulta", "nike"];
const ROW_STORES = ROW_SLUGS.map((slug) => getPublicDealsRetailer(slug)).filter(
  (retailer): retailer is PublicDealsRetailer => Boolean(retailer),
);
const ROW_SIZE = 6;

/** Big-box chains are named generically on the public home page. */
const IN_STORE_NAMES: Record<string, string> = { home_depot: "Big Box Store" };
function inStoreName(chainId: string, fallback: string): string {
  return IN_STORE_NAMES[chainId] || fallback;
}

/** `product` leads the Online deals row; `tileProduct` (the next-best find) pictures the store tile so the two rows don't repeat. */
type StoreDeals = {
  retailer: PublicDealsRetailer;
  product: ProductListItem | null;
  tileProduct: ProductListItem | null;
  /** The store's "Top from …" row: its biggest discounts after `product`, non-clothing first. */
  rowProducts: ProductListItem[];
  topOff: number;
  total: number;
};

/**
 * Carrier-locked phones list at $5–$15 "99% off" (the price assumes a carrier plan), so on the
 * home page they'd crowd every row. They stay on the store's own page.
 */
const CARRIER_PHONE = /^(t-mobile|at&t|verizon|straight talk|total by verizon|tracfone|boost|cricket|metro by t-mobile|visible|simple mobile)\b/i;

async function loadStoreDeals(retailer: PublicDealsRetailer): Promise<StoreDeals> {
  try {
    const page = await browsePublicProducts({
      retailer: retailer.apiRetailer,
      storeId: retailer.storeId,
      searchTokens: [],
      categories: [],
      status: "all",
      sortBy: "discountPercentage",
      sortDirection: "desc",
      limit: 24,
    });
    // Rotate each fetch so the lead deal and rows change between visits.
    const products = rotateDeals((page.products || []).filter((item) => !CARRIER_PHONE.test(item.name || "")));
    const product = products.find((item) => !isApparel(item.name) && getGuestDealPrice(item) >= 5 && Boolean(item.image)) || null;
    const tileProduct =
      products.find((item) => item !== product && !isApparel(item.name) && Boolean(item.image)) ||
      products.find((item) => item !== product && Boolean(item.image)) ||
      product;
    const topOff = products.reduce((best, item) => Math.max(best, getGuestDealDiscount(item)), 0);
    const rest = products.filter((item) => item !== product && Boolean(item.image) && getGuestDealPrice(item) > 0);
    // Non-clothing finds of $5+ first, so a row doesn't open on 94¢ earrings.
    const rank = (item: ProductListItem) => (isApparel(item.name) ? 2 : 0) + (getGuestDealPrice(item) < 5 ? 1 : 0);
    const rowProducts = [...rest].sort((x, y) => rank(x) - rank(y)).slice(0, ROW_SIZE);
    return { retailer, product, tileProduct, rowProducts, topOff, total: page.total || 0 };
  } catch {
    return { retailer, product: null, tileProduct: null, rowProducts: [], topOff: 0, total: 0 };
  }
}

/**
 * The best non-clothing deal at each featured online store, with that store's deal count.
 * One query per store, so each row renders as soon as its own store answers instead of
 * the whole page waiting on the slowest one. `settled` lists stores in ROW_STORES order.
 */
function useOnlineStoreDeals() {
  return useQueries({
    queries: ROW_STORES.map((retailer) => ({
      queryKey: ["home-landing-online", retailer.slug],
      staleTime: 5 * 60_000,
      retry: 1,
      queryFn: () => loadStoreDeals(retailer),
    })),
    combine: (results) => ({
      settled: results.flatMap((result) => (result.data ? [result.data] : [])),
      pending: results.some((result) => result.isPending),
    }),
  });
}

/**
 * Member hauls shown when the public teaser endpoint isn't reachable: real posts with their own
 * photos, captions quoted as written (community-successes, Aug–Oct 2026).
 */
const FALLBACK_HAULS: PublicMemberHaul[] = [
  {
    postId: "discord-1562933256494518342",
    imageUrl: "https://dealhawk-images.s3.us-east-2.amazonaws.com/community-successes/discord-1562933256494518342/1.png",
    message: "Gracias DealHawk Pro ☺️Target wagon de $109 en liquidación por $33.00 pero me lo llevé por $24.75 porque tenía un cupón de 25% off.",
    createdAt: 1792702726244,
  },
  {
    postId: "84f20706-eb58-425a-9e27-4949dadf2530",
    imageUrl: "https://dealhawk-images.s3.us-east-2.amazonaws.com/community-successes/84f20706-eb58-425a-9e27-4949dadf2530/1.jpg",
    message: "Was able to score all the backpacks in a $1.00 thanks to the DealHawk app!!!",
    createdAt: 1792691412834,
  },
  {
    postId: "discord-1544415160104525854",
    imageUrl: "https://dealhawk-images.s3.us-east-2.amazonaws.com/community-successes/discord-1544415160104525854/1.jpg",
    message: "Walmart clearance $2.50\nCash back $6.00 ( buy 3 ) \nFinal cost $0.50",
    createdAt: 1788287668015,
  },
  {
    postId: "discord-1538606545183441027",
    imageUrl: "https://dealhawk-images.s3.us-east-2.amazonaws.com/community-successes/discord-1538606545183441027/1.jpg",
    message: "Just picked up my $549 vac for $89 during the Lowes glitch last night",
    createdAt: 1786902786299,
  },
];

const OFFER_SEEN_KEY = "dh_home_offer_seen";
// The first-month offer waits until the visitor has looked around: scrolled past
// the first rows, or stayed a while. Popping it before any deal is on screen
// asks for money before we've shown a reason.
const OFFER_DWELL_MS = 30_000;
const OFFER_SCROLL_VIEWPORTS = 1.2;

type JoinPrompt = { reason: "offer" } | { reason: "watch"; itemName: string };

export default function HomeLanding({
  zip,
  previewDeals,
  previewLoading,
  exampleDeals,
  onSubmitZip,
  onOpenDeal,
  localSignupUrl,
  onSignup,
  freeMember = false,
}: {
  zip: string;
  previewDeals: PublicPreviewDeal[];
  previewLoading: boolean;
  exampleDeals: PublicPreviewDeal[];
  onSubmitZip: (zip: string) => void;
  onOpenDeal: (product: ProductListItem, retailer: PublicDealsRetailer) => void;
  localSignupUrl: (source: string) => string;
  onSignup: (trigger: string) => void;
  /** Signed in on a free account: CTAs say Upgrade, never Join. */
  freeMember?: boolean;
}) {
  const joinLabel = freeMember ? "Upgrade" : "Join";
  const online = useOnlineStoreDeals();
  const allStoreDeals = online.settled;
  const storeDeals = allStoreDeals.filter((entry) => ONLINE_SLUGS.includes(entry.retailer.slug));
  // Store order rotates too, so the six-card row isn't always led by the same store. The order
  // is drawn once per visit so cards fill in place as stores answer instead of reshuffling.
  const [onlineOrder] = useState(() => rotateDeals(ONLINE_SLUGS));
  const onlineDeals = useMemo(
    () =>
      onlineOrder
        .map((slug) => storeDeals.find((entry) => entry.retailer.slug === slug && entry.product))
        .filter((entry): entry is StoreDeals => Boolean(entry))
        .slice(0, 6),
    [onlineOrder, storeDeals],
  );
  // Skeletons hold the row's slots only while a featured store could still fill them.
  const onlineSkeletons = online.pending ? Math.max(0, 6 - onlineDeals.length) : 0;
  const usingExamples = !zip || (!previewLoading && previewDeals.length === 0);
  const clearance = prioritizeNonApparel(usingExamples ? exampleDeals : previewDeals).slice(0, 6);
  const heroDeals = clearance.filter((deal) => deal.image).slice(0, 3);
  const [editingZip, setEditingZip] = useState(false);
  const clearanceChain = usingExamples ? "Walmart" : inStoreName(clearance[0]?.retailer || "", clearance[0]?.retailer === "walmart" ? "Walmart" : "");
  const clearanceTitle = clearanceChain ? `Clearance at ${clearanceChain} near you` : "Clearance near you";
  const unlockUrl = localSignupUrl("home_landing_unlock");

  // One real deal near the visitor, shown in full: price, store, aisle, stock.
  const freeDealQuery = useQuery({ ...publicFreeDealQuery(zip, "walmart"), enabled: /^\d{5}$/.test(zip) });
  const freeDeal = freeDealQuery.data?.freeDeal ?? null;
  const moreDeals = Math.max(0, (freeDealQuery.data?.totalDeals ?? 0) - 1);
  useEffect(() => {
    if (freeDeal) track("free_deal_shown", { surface: "web", retailer: freeDeal.retailer, discount: freeDeal.discountPercent });
  }, [freeDeal]);

  const haulsQuery = useQuery({
    queryKey: ["public-member-hauls"],
    staleTime: 10 * 60_000,
    retry: 0,
    queryFn: getPublicMemberHauls,
  });
  const hauls = (haulsQuery.data?.length ? haulsQuery.data : haulsQuery.isPending ? [] : FALLBACK_HAULS).slice(0, 4);

  // Join prompt: once per visitor after a short look around, and whenever someone taps Watch.
  // LogFrog only loads on the production origins (index.html disables it elsewhere).
  const [chatAvailable] = useState(() => typeof window !== "undefined" && window.__logfrogDisabled !== true);
  const [prompt, setPrompt] = useState<JoinPrompt | null>(null);
  // The daily clearance spotlight and the join prompt never stack: each waits for the other.
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  useEffect(() => {
    let seen = false;
    try {
      seen = window.localStorage.getItem(OFFER_SEEN_KEY) === "1";
    } catch {
      seen = true;
    }
    if (seen) return;
    let shown = false;
    const show = (trigger: "dwell" | "scroll") => {
      if (shown) return;
      shown = true;
      cleanup();
      setPrompt((current) => current ?? { reason: "offer" });
      track("home_offer_prompt_shown", { surface: "web", trigger });
    };
    // The feed scrolls inside its own container on some layouts, so listen in
    // the capture phase and read whichever element (or the window) scrolled.
    const onScroll = (event: Event) => {
      const target = event.target;
      const scrolled = target instanceof Element ? target.scrollTop : window.scrollY;
      if (scrolled > window.innerHeight * OFFER_SCROLL_VIEWPORTS) show("scroll");
    };
    const timer = window.setTimeout(() => show("dwell"), OFFER_DWELL_MS);
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    function cleanup() {
      window.clearTimeout(timer);
      document.removeEventListener("scroll", onScroll, { capture: true });
    }
    return cleanup;
  }, []);
  const closePrompt = () => {
    try {
      window.localStorage.setItem(OFFER_SEEN_KEY, "1");
    } catch {
      // Private mode: the prompt may show again next visit.
    }
    setPrompt(null);
  };
  const watch = (itemName: string) => {
    track("home_watch_tapped", { surface: "web" });
    setPrompt({ reason: "watch", itemName });
  };

  return (
    <div id="top" className="pb-28 lg:pb-0" style={{ fontFamily: LANDING_FONT, color: HF.ink }} data-testid="home-landing">
      <LandingNav stores={ONLINE_STORES} />

      <div className="px-4 pt-5 sm:px-8 sm:pt-8">
      <div className="mx-auto flex max-w-[1320px] flex-col gap-11 sm:gap-[52px]">
        {/* Hero: the in-store offer and the ZIP that unlocks "near you" */}
        <section
          id="clearance-near-you"
          className="grid scroll-mt-28 grid-cols-1 items-center gap-6 rounded-[24px] px-5 pb-5 pt-6 sm:rounded-[28px] sm:p-10 lg:grid-cols-2 lg:gap-8 lg:px-11"
          style={{ background: "var(--hf-hero)" }}
        >
          <div className="flex max-w-[520px] flex-col gap-3.5 sm:gap-[18px]">
            <span className="text-[14px] font-semibold sm:text-[15px]" style={{ color: HF.brandText }}>In-store clearance</span>
            <h1 className="m-0 text-[34px] font-bold leading-[1.04] tracking-[-1px] sm:text-[48px] sm:leading-[1.02] sm:tracking-[-1.4px]">
              Up to 70% off on the shelf near you
            </h1>
            <p className="m-0 hidden text-[17px] leading-[1.45] sm:block" style={{ color: HF.body }}>
              Walmart, Target, Best Buy and more. See what’s marked down at your stores before you drive.
            </p>
            {zip && !editingZip ? (
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[15px]" style={{ color: HF.body }} data-testid="landing-zip-set">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={HF.brandText} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></svg>
                Showing stores near <b style={{ color: HF.ink }}>{zip}</b>
                <button type="button" onClick={() => setEditingZip(true)} className="font-semibold underline underline-offset-[3px]" style={{ color: HF.brandText }}>Change</button>
              </span>
            ) : (
              <ZipForm
                key={zip}
                initialZip={zip}
                onSubmit={(value) => {
                  setEditingZip(false);
                  onSubmitZip(value);
                }}
              />
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3.5">
            {heroDeals.length === 0
              ? Array.from({ length: 3 }, (_, i) => <div key={i} className="aspect-[3/4] animate-pulse rounded-[14px] sm:rounded-[18px]" style={{ background: HF.surface }} />)
              : heroDeals.map((deal, index) => (
                  <Link
                    key={`${deal.name}-${index}`}
                    to={unlockUrl}
                    onClick={() => onSignup("landing_hero_tile")}
                    className="flex flex-col gap-1.5 rounded-[14px] bg-white p-2 no-underline shadow-[0_1px_2px_rgba(23,19,31,0.06),0_8px_24px_rgba(58,10,99,0.08)] sm:gap-2.5 sm:rounded-[18px] sm:p-3.5"
                    style={{ color: "#4A4552" }}
                    aria-label={`${cleanName(deal.name)}, ${deal.discountBand}`}
                  >
                    <span className="relative block aspect-square">
                      {deal.image && <ProductTileImage src={getProductImageUrl(deal.image, "400")} />}
                    </span>
                    <span className="self-start rounded-[5px] px-1.5 py-px text-[12px] font-bold sm:rounded-md sm:px-2 sm:text-[15px]" style={{ background: HIGHLIGHT, color: HIGHLIGHT_INK }}>{deal.discountBand}</span>
                    <span className="line-clamp-2 hidden text-[13px] leading-[1.3] sm:block">{cleanName(deal.name)}</span>
                  </Link>
                ))}
          </div>
        </section>

        {freeDeal && (
          <FreeDealCard
            deal={freeDeal}
            moreDeals={moreDeals}
            unlockUrl={localSignupUrl("home_free_deal")}
            onUnlock={() => {
              track("free_deal_unlock_tapped", { surface: "web", retailer: freeDeal.retailer });
              onSignup("landing_free_deal");
            }}
          />
        )}

        {/* In-store clearance near the shopper */}
        <section id="clearance-row" data-testid="landing-clearance">
          <SectionHead
            title={clearanceTitle}
            note={
              usingExamples ? (
                <span style={{ color: MUTED }}>{zip && !previewLoading ? `Nothing on clearance near ${zip} right now. Real finds from other stores.` : "Examples. Add your ZIP to see your stores."}</span>
              ) : undefined
            }
            action={<Link to={unlockUrl} onClick={() => onSignup("landing_clearance_all")} className="text-[15px] font-semibold no-underline hover:underline" style={{ color: HF.brandText }}>See all</Link>}
          />
          <div className="scrollbar-hide -mx-4 flex gap-3.5 overflow-x-auto px-4 sm:mx-0 sm:grid sm:grid-cols-3 sm:gap-x-[22px] sm:gap-y-6 sm:overflow-visible sm:px-0 lg:grid-cols-4 xl:grid-cols-6">
            {zip && previewLoading
              ? Array.from({ length: 6 }, (_, i) => <div key={i} className="aspect-square w-[148px] flex-none animate-pulse rounded-[16px] sm:w-auto" style={{ background: SKELETON }} />)
              : clearance.map((deal, index) => (
                  <div key={`${deal.name}-${index}`} className={`${index >= 4 ? "sm:hidden xl:flex" : ""} relative flex w-[148px] flex-none flex-col sm:w-auto`} data-testid="landing-clearance-card">
                    <Link
                      to={unlockUrl}
                      onClick={() => onSignup("landing_clearance_card")}
                      className="group flex flex-col gap-1 no-underline"
                      style={{ color: HF.ink }}
                    >
                      <span className="relative block aspect-square overflow-hidden rounded-[16px]" style={{ background: CARD_TILE }}>
                        {deal.image && <ProductTileImage src={getProductImageUrl(deal.image, "400")} className="mix-blend-multiply transition-transform duration-200 group-hover:scale-[1.03]" />}
                      </span>
                      <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="text-[16px] font-bold sm:text-[17px]" style={{ color: HF.price }}>{deal.discountBand}</span>
                        <span className="flex items-center gap-1 text-[12px] sm:text-[13px]" style={{ color: MUTED }}>
                          <LockGlyph size={12} />
                          Member price
                        </span>
                      </span>
                      <span className="line-clamp-2 text-[14px] leading-[1.3] sm:text-[15px]">{cleanName(deal.name)}</span>
                      <span className="text-[12.5px] sm:text-[13px]" style={{ color: MUTED }}>
                        {usingExamples ? `Example · ${inStoreName(deal.retailer, deal.retailer === "walmart" ? "Walmart" : "a store")}` : `${inStoreName(deal.retailer, deal.retailer === "walmart" ? "Walmart" : "In store")} near you`}
                      </span>
                    </Link>
                    <WatchButton label={cleanName(deal.name)} onClick={() => watch(cleanName(deal.name))} />
                  </div>
                ))}
          </div>
        </section>

        {/* Online deals across stores */}
        <section id="online-deals" className="scroll-mt-28">
          <SectionHead
            title="Biggest drops online"
            note={<span style={{ color: MUTED }}>Free to browse</span>}
            action={<Link to="/stores" className="text-[15px] font-semibold no-underline hover:underline" style={{ color: HF.brandText }}>See all</Link>}
          />
          <div className="grid grid-cols-2 gap-x-3.5 gap-y-6 sm:grid-cols-3 sm:gap-x-[22px] lg:grid-cols-4 xl:grid-cols-6">
            {onlineDeals.map(({ retailer, product }, index) => (
              <OnlineDealCard
                key={retailer.slug}
                item={product as ProductListItem}
                retailer={retailer}
                onOpen={onOpenDeal}
                onWatch={watch}
                className={index >= 4 ? "hidden xl:flex" : "flex"}
                testId="landing-online-card"
              />
            ))}
            {Array.from({ length: onlineSkeletons }, (_, i) => (
              <div
                key={`skeleton-${i}`}
                className={`${onlineDeals.length + i >= 4 ? "hidden xl:block" : ""} aspect-square animate-pulse rounded-[16px]`}
                style={{ background: SKELETON }}
              />
            ))}
          </div>
        </section>

        {/* The app, with its own screens */}
        <AppShowcase />

        {/* Member hauls: real posts, the member's photo and own words */}
        <section id="member-hauls" className="scroll-mt-28" data-testid="landing-member-hauls">
          <SectionHead
            title="What members brought home"
            action={<Link to={unlockUrl} onClick={() => onSignup("landing_hauls_all")} className="text-[15px] font-semibold no-underline hover:underline" style={{ color: HF.brandText }}>See every haul ›</Link>}
          />
          <div className="scrollbar-hide -mx-4 flex gap-3 overflow-x-auto px-4 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0 lg:grid-cols-4">
            {haulsQuery.isPending
              ? Array.from({ length: 4 }, (_, i) => <div key={i} className="aspect-[4/5] w-[270px] flex-none animate-pulse rounded-[20px] sm:w-auto" style={{ background: SKELETON }} />)
              : hauls.map((haul) => (
                  <figure key={haul.postId} className="relative m-0 aspect-[4/5] w-[270px] flex-none overflow-hidden rounded-[20px] sm:w-auto" style={{ background: TILE }} data-testid="landing-haul">
                    <img src={haul.imageUrl} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
                    <figcaption className="absolute inset-x-2.5 bottom-2.5 rounded-[14px] px-3.5 py-3 sm:inset-x-3 sm:bottom-3 sm:px-4" style={{ background: HF.surface, color: HF.ink }}>
                      <span className="line-clamp-3 whitespace-pre-line text-[14px] leading-[1.35]">“{haul.message.trim()}”</span>
                    </figcaption>
                  </figure>
                ))}
          </div>
        </section>

        {/* One row per store: its biggest online discounts */}
        {allStoreDeals
          .filter((entry) => entry.rowProducts.length >= 3)
          .slice(0, 4)
          .map(({ retailer, rowProducts, total }) => (
            <section key={retailer.slug} data-testid="landing-store-row">
              <SectionHead
                title={`Top from ${retailer.displayName ?? retailer.shortName}`}
                note={total > 0 ? <span style={{ color: MUTED }}>{total.toLocaleString()} deals</span> : undefined}
                action={<Link to={`/${retailer.slug}`} className="text-[15px] font-semibold underline underline-offset-[3px]" style={{ color: HF.ink }}>Show all ›</Link>}
              />
              <div className="scrollbar-hide flex gap-4 overflow-x-auto sm:grid sm:grid-cols-3 sm:gap-x-5 sm:gap-y-6 sm:overflow-visible lg:grid-cols-4 xl:grid-cols-6">
                {rowProducts.map((item, index) => (
                  <OnlineDealCard
                    key={`${retailer.slug}-${index}`}
                    item={item}
                    retailer={retailer}
                    onOpen={onOpenDeal}
                    onWatch={watch}
                    className={`${index >= 3 ? "sm:hidden lg:flex" : ""} ${index >= 4 ? "lg:hidden xl:flex" : ""} flex w-[44%] flex-none sm:w-auto`}
                  />
                ))}
              </div>
            </section>
          ))}
        {online.pending &&
          Array.from({ length: allStoreDeals.length ? 1 : 2 }, (_, i) => (
            <section key={i} aria-hidden="true">
              <div className="mb-4 h-7 w-56 animate-pulse rounded-lg" style={{ background: SKELETON }} />
              <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 xl:grid-cols-6">
                {Array.from({ length: ROW_SIZE }, (_, j) => <div key={j} className={`${j >= 2 ? "hidden sm:block" : ""} aspect-square animate-pulse rounded-[18px]`} style={{ background: SKELETON }} />)}
              </div>
            </section>
          ))}

        {/* Stores, Instacart's "Closest to you" style: top deal photo, deal count, best discount */}
        <section>
          <SectionHead
            title="Stores with the biggest drops"
            action={<Link to="/stores" className="text-[15px] font-semibold underline underline-offset-[3px]" style={{ color: HF.ink }}>All 60+ stores</Link>}
          />
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
            {(storeDeals.length ? [...storeDeals].sort((a, b) => b.topOff - a.topOff) : ONLINE_STORES.map((retailer) => ({ retailer, tileProduct: null, topOff: 0, total: 0 }))).slice(0, 6).map(
              ({ retailer, tileProduct, topOff, total }) => (
                <Link
                  key={retailer.slug}
                  to={`/${retailer.slug}`}
                  aria-label={`${retailerLabel(retailer)}${topOff > 0 ? `, up to ${topOff}% off` : ""}${total > 0 ? `, ${total.toLocaleString()} deals` : ""}`}
                  className="flex items-center gap-3.5 rounded-[14px] border p-3.5 no-underline transition-shadow hover:shadow-[0_6px_18px_rgba(31,26,46,0.08)]"
                  style={{ borderColor: PANEL_BORDER, color: HF.ink }}
                  data-testid="landing-store-tile"
                >
                  <span className="relative flex h-[68px] w-[68px] flex-shrink-0 items-center justify-center overflow-hidden rounded-[12px] border bg-white" style={{ borderColor: PANEL_BORDER }}>
                    {tileProduct?.image ? (
                      <ProductTileImage src={getProductImageUrl(tileProduct.image, "200")} />
                    ) : (
                      online.pending && <span className="absolute inset-0 animate-pulse" style={{ background: TILE }} />
                    )}
                  </span>
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate text-[17px] font-bold">{retailerLabel(retailer)}</span>
                    <span className="text-[14px] font-medium" style={{ color: HF.price }}>{total > 0 ? `${total.toLocaleString()} deals online` : "Online deals"}</span>
                    <span className="flex flex-wrap items-center gap-1.5 text-[13.5px]" style={{ color: MUTED }}>
                      Free to browse
                      {topOff > 0 && <span className="rounded-[4px] px-1 text-[13px] font-semibold" style={{ background: HIGHLIGHT, color: HIGHLIGHT_INK }}>Up to {topOff}% off</span>}
                    </span>
                  </span>
                </Link>
              ),
            )}
          </div>
        </section>

        {/* Pricing, and the Penny Meals perk every plan includes */}
        <section id="pricing" className="scroll-mt-28" data-testid="landing-pricing">
          <SectionHead
            title="Pricing"
            note={<span style={{ color: MUTED }}>50% off your first month on every plan · cancel anytime</span>}
          />
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3 sm:gap-5">
            {PRICING_PLANS.map((plan) => (
              <div
                key={plan.id}
                className="flex flex-col gap-4 rounded-[20px] border p-5 sm:rounded-[22px] sm:p-6"
                style={{ borderColor: plan.highlight ? HF.ink : PANEL_BORDER, borderWidth: plan.highlight ? 2 : 1 }}
                data-testid={`landing-plan-${plan.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[18px] font-extrabold tracking-[-0.3px]">{plan.name}</span>
                  {plan.highlight && (
                    <span className="rounded-full px-2.5 py-0.5 text-[12px] font-bold" style={{ background: HF.inkBtn, color: HF.onInkBtn }}>Most stores</span>
                  )}
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="flex items-baseline gap-2">
                    <span className="text-[34px] font-extrabold leading-[38px] tracking-[-1px] tabular-nums">${formatPlanPrice(plan.intro)}</span>
                    <span className="text-[14px] font-semibold" style={{ color: MUTED }}>first month</span>
                  </span>
                  <span className="text-[13.5px]" style={{ color: MUTED }}>then ${formatPlanPrice(plan.price)}/mo</span>
                </div>
                <ul className="m-0 flex list-none flex-col gap-2 p-0 text-[14px] leading-5" style={{ color: HF.body }}>
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-2">
                      <CheckIcon />
                      {feature}
                    </li>
                  ))}
                  <li className="flex gap-2 font-semibold" style={{ color: HF.ink }}>
                    <CheckIcon />
                    Penny Meals Plus, free
                  </li>
                </ul>
                <Link
                  to={unlockUrl}
                  onClick={() => onSignup(`landing_pricing_${plan.id}`)}
                  className="mt-auto flex h-12 items-center justify-center rounded-full text-[15px] font-bold no-underline hover:opacity-90"
                  style={plan.highlight ? { background: HF.inkBtn, color: HF.onInkBtn } : { background: SECTION, color: HF.ink, border: "1px solid var(--hf-plan-border)" }}
                >
                  Get 50% off {plan.name}
                </Link>
              </div>
            ))}
          </div>
          <a
            href="https://pennymeals.com/?utm_source=dealhawk&utm_medium=web&utm_campaign=landing_pricing"
            target="_blank"
            rel="noopener"
            className="mt-3.5 flex items-center gap-4 rounded-[20px] p-4 no-underline sm:mt-5 sm:gap-5 sm:rounded-[22px] sm:p-5"
            style={{ background: "var(--hf-rose-soft)", color: HF.ink }}
            data-testid="landing-penny-meals"
            onClick={() => track("penny_meals_promo_tapped", { placement: "landing_pricing", isMember: false })}
          >
            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[14px] bg-white sm:h-14 sm:w-14">
              <img src="/penny-meals-mark.png" alt="" className="h-8 w-8 object-contain sm:h-9 sm:w-9" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-[16px] font-extrabold sm:text-[18px]">Every paid plan includes Penny Meals Plus, free</span>
              <span className="text-[13.5px] leading-5 sm:text-[14.5px]" style={{ color: "var(--hf-rose-text)" }}>
                Turn grocery markdowns near you into cheap dinners. Sign in at pennymeals.com with your DealHawk account.
              </span>
            </span>
            <span className="hidden flex-shrink-0 text-[14.5px] font-bold sm:block" style={{ color: HF.red }}>Learn more →</span>
          </a>
        </section>
      </div>
      </div>

      {/* Phone: the offer stays one tap away while scrolling */}
      <div
        className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 px-4 pb-3.5 pt-2.5 shadow-[0_-6px_20px_rgba(23,19,31,0.10)] lg:hidden"
        style={{ background: HF.surface, color: HF.ink, paddingBottom: "calc(14px + env(safe-area-inset-bottom, 0px))", fontFamily: LANDING_FONT }}
        data-testid="landing-mobile-bar"
      >
        <span className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="text-[16px] font-bold">{BASIC_INTRO_LABEL} first month</span>
          <span className="truncate text-[12px]" style={{ color: MUTED }}>then {BASIC_PRICE_LABEL}/mo · cancel anytime</span>
        </span>
        <Link
          to={unlockUrl}
          onClick={() => onSignup("landing_mobile_bar")}
          className="flex h-[46px] flex-shrink-0 items-center rounded-full px-[22px] text-[15px] font-bold text-white no-underline"
          style={{ background: PURPLE }}
        >
          {joinLabel}
        </Link>
      </div>

      {/* Desktop: support chat, bottom-right (LogFrog has no resting launcher of its own) */}
      {chatAvailable && (
        <button
          type="button"
          onClick={() => {
            track("home_chat_opened", { surface: "web" });
            openLogfrogChat();
          }}
          className="fixed bottom-6 right-6 z-30 hidden h-12 items-center gap-2 rounded-full pl-4 pr-5 text-[15px] font-bold text-white shadow-[0_10px_28px_rgba(59,10,102,0.35)] transition-transform hover:scale-[1.03] lg:flex"
          style={{ background: PURPLE }}
          data-testid="landing-chat"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z" /></svg>
          Chat with us
        </button>
      )}

      <ClearanceSpotlight
        zip={zip}
        onSubmitZip={onSubmitZip}
        unlockUrl={localSignupUrl("daily_spotlight")}
        onUnlock={onSignup}
        blocked={prompt !== null}
        onOpenChange={setSpotlightOpen}
      />
      {/* An offer prompt that comes due while the spotlight is up waits for it to close. */}
      {prompt && !spotlightOpen && (
        <JoinPrompt
          prompt={prompt}
          joinLabel={joinLabel}
          to={localSignupUrl(prompt.reason === "watch" ? "home_watch" : "home_offer_prompt")}
          onContinue={() => {
            onSignup(prompt.reason === "watch" ? "landing_watch" : "landing_offer_prompt");
            closePrompt();
          }}
          onClose={closePrompt}
        />
      )}
    </div>
  );
}

/** Round "+" on a card's photo: the way in for Watches. Logged-out shoppers get the join prompt. */
function WatchButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    // Sits over the card's square photo (the card's top square), bottom-right.
    <span className="pointer-events-none absolute inset-x-0 top-0 aspect-square">
      <button
        type="button"
        onClick={onClick}
        aria-label={`Watch ${label}`}
        title="Watch"
        className="pointer-events-auto absolute bottom-2 right-2 flex h-9 w-9 items-center justify-center rounded-full text-white shadow-[0_2px_6px_rgba(58,10,99,0.3)] transition hover:brightness-125 sm:bottom-2.5 sm:right-2.5 sm:h-10 sm:w-10"
        style={{ background: PURPLE }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      </button>
    </span>
  );
}

/** The join prompt: the intro offer on first visit, or the reason a Watch needs an account. */
function JoinPrompt({ prompt, joinLabel, to, onContinue, onClose }: { prompt: JoinPrompt; joinLabel: string; to: string; onContinue: () => void; onClose: () => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const watching = prompt.reason === "watch";
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="presentation">
      <div aria-hidden="true" onClick={onClose} className="absolute inset-0" style={{ background: "var(--hf-overlay)" }} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="join-prompt-title"
        className="relative flex w-full max-w-[460px] flex-col items-center gap-4 rounded-t-[22px] px-6 pb-6 pt-14 text-center sm:rounded-[22px] sm:px-9"
        style={{ background: HF.surface, color: HF.ink }}
        data-testid="join-prompt"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-[var(--hf-hover-strong)]"
          style={{ background: SECTION, color: HF.ink }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
        {watching ? (
          <h2 id="join-prompt-title" className="m-0 text-[24px] font-extrabold leading-[30px] tracking-[-0.6px] sm:text-[28px] sm:leading-[34px]">
            Get a ping when it drops near you
          </h2>
        ) : (
          <h2 id="join-prompt-title" className="m-0 text-[26px] font-extrabold leading-[32px] tracking-[-0.7px] sm:text-[30px] sm:leading-[36px]">
            <mark className="rounded px-2" style={{ background: GOLD, color: INK }}>{BASIC_INTRO_LABEL}</mark>
            <br />
            for your first month
          </h2>
        )}
        <p className="m-0 max-w-[380px] text-[15px] leading-[22px]" style={{ color: HF.body }}>
          {watching
            ? `Members can watch “${prompt.itemName}” and get an alert when it’s marked down at their stores. ${BASIC_INTRO_LABEL} first month, then ${BASIC_PRICE_LABEL}/mo.`
            : `See the price, aisle and how many are left on clearance at stores near you. Then ${BASIC_PRICE_LABEL}/mo, cancel anytime.`}
        </p>
        <Link
          to={to}
          onClick={onContinue}
          className="flex h-[52px] w-full items-center justify-center rounded-full text-[16px] font-bold no-underline hover:opacity-90"
          style={{ background: HF.inkBtn, color: HF.onInkBtn }}
        >
          {watching ? `${joinLabel} and watch it` : "Get 50% off"}
        </Link>
        <button type="button" onClick={onClose} className="-mt-1 py-1.5 text-[15px] font-semibold underline underline-offset-2" style={{ color: HF.text2 }}>
          {watching ? "Not now" : "No thanks, keep browsing"}
        </button>
        <span className="w-full border-t pt-3.5 text-[13px]" style={{ borderColor: PANEL_BORDER, color: MUTED }}>
          Online deals from 60+ stores stay free. No account needed.
        </span>
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--hf-green)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className="mt-px flex-shrink-0" aria-hidden="true">
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

/** An online deal: photo on a grey tile with the % off, price over the struck regular price, name, store. */
function OnlineDealCard({
  item,
  retailer,
  onOpen,
  onWatch,
  className,
  testId,
}: {
  item: ProductListItem;
  retailer: PublicDealsRetailer;
  onOpen: (product: ProductListItem, retailer: PublicDealsRetailer) => void;
  onWatch: (itemName: string) => void;
  className: string;
  testId?: string;
}) {
  const price = getGuestDealPrice(item);
  const msrp = Number(item.msrp) || 0;
  const off = getGuestDealDiscount(item);
  const onSale = msrp > price && price > 0;
  const name = cleanName(item.name);
  return (
    <div className={`${className} relative flex-col`} data-testid={testId}>
      <button type="button" onClick={() => onOpen(item, retailer)} className="group flex flex-col gap-1 text-left">
        <span className="relative block aspect-square w-full overflow-hidden rounded-[16px]" style={{ background: CARD_TILE }}>
          {item.image && <ProductTileImage src={getProductImageUrl(item.image, "400")} className="mix-blend-multiply transition-transform duration-200 group-hover:scale-[1.03]" />}
          {off > 0 && (
            <span className="absolute left-2 top-2 rounded-full px-2 py-0.5 text-[12px] font-bold text-white sm:left-2.5 sm:top-2.5 sm:text-[13px]" style={{ background: DEAL_GREEN }}>{off}% off</span>
          )}
        </span>
        <span className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          {price > 0 ? <SalePrice value={price} /> : <span className="text-[16px] font-bold">See price</span>}
          {onSale && <span className="text-[13px] line-through sm:text-[14px]" style={{ color: MUTED }}>{money.format(msrp)}</span>}
        </span>
        <span className="line-clamp-2 text-[14px] leading-[1.3] sm:text-[15px]">{name}</span>
        <span className="text-[12.5px] sm:text-[13px]" style={{ color: MUTED }}>{retailerLabel(retailer)}</span>
      </button>
      <WatchButton label={name} onClick={() => onWatch(name)} />
    </div>
  );
}

/** The one in-store deal shown in full after a ZIP: the proof that the locked rows are real. */
function FreeDealCard({ deal, moreDeals, unlockUrl, onUnlock }: { deal: PublicFreeDeal; moreDeals: number; unlockUrl: string; onUnlock: () => void }) {
  const chain = inStoreName(deal.retailer, deal.retailer === "walmart" ? "Walmart" : "Your store");
  const where = [chain, deal.distanceMiles ? `${deal.distanceMiles} mi` : "", deal.storeAddress ?? ""].filter(Boolean).join(" · ");
  const shelf = [deal.aisle ? `Aisle ${deal.aisle}` : "", deal.quantity ? `${deal.quantity} left` : ""].filter(Boolean).join(" · ");
  return (
    <section
      data-testid="landing-free-deal"
      className="grid grid-cols-[96px_1fr] gap-4 rounded-[22px] border p-4 sm:grid-cols-[180px_1fr_auto] sm:items-center sm:gap-7 sm:p-6"
      style={{ borderColor: PANEL_BORDER, background: HF.surface }}
    >
      <span className="relative block aspect-square overflow-hidden rounded-[14px]" style={{ background: CARD_TILE }}>
        {deal.image && <ProductTileImage src={getProductImageUrl(deal.image, "400")} className="mix-blend-multiply" />}
      </span>
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="text-[13px] font-semibold sm:text-[14px]" style={{ color: HF.brandText }}>Free look: one real deal near you</span>
        <span className="line-clamp-2 text-[16px] font-semibold leading-[1.3] sm:text-[19px]" style={{ color: HF.ink }}>{cleanName(deal.name)}</span>
        <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <span className="text-[26px] font-bold leading-none sm:text-[32px]" style={{ color: DEAL_GREEN }}>{money.format(deal.price)}</span>
          <span className="text-[14px] line-through sm:text-[15px]" style={{ color: MUTED }}>Regular {money.format(deal.regularPrice)}</span>
          <span className="rounded-md px-2 py-px text-[13px] font-bold sm:text-[14px]" style={{ background: HIGHLIGHT, color: HIGHLIGHT_INK }}>{deal.discountPercent}% off</span>
        </span>
        <span className="text-[13.5px] sm:text-[14.5px]" style={{ color: HF.body }}>{where}</span>
        {shelf && <span className="text-[13.5px] font-semibold sm:text-[14.5px]" style={{ color: HF.ink }}>{shelf}</span>}
      </div>
      <div className="col-span-2 flex flex-col gap-1.5 sm:col-span-1 sm:max-w-[240px]">
        <Link
          to={unlockUrl}
          onClick={onUnlock}
          className="rounded-[14px] px-5 py-3 text-center text-[16px] font-bold text-white no-underline"
          style={{ background: PURPLE }}
        >
          {moreDeals > 0 ? `See ${moreDeals} more near you` : "See every deal near you"}
        </Link>
        <span className="text-center text-[12.5px] sm:text-[13px]" style={{ color: MUTED }}>Members see the price, aisle and stock for all of them.</span>
      </div>
    </section>
  );
}

function SectionHead({ title, note, action }: { title: string; note?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3.5 flex flex-wrap items-baseline gap-x-3 gap-y-1 sm:mb-[18px]">
      <h2 className="m-0 text-[22px] font-bold tracking-[-0.4px] sm:text-[28px] sm:tracking-[-0.6px]" style={{ color: HF.ink }}>{title}</h2>
      {note && <span className="text-[14px] sm:text-[15px]">{note}</span>}
      <span className="flex-1" />
      {action}
    </div>
  );
}

function ZipForm({ initialZip, onSubmit }: { initialZip: string; onSubmit: (zip: string) => void }) {
  const [value, setValue] = useState(initialZip);
  const [invalid, setInvalid] = useState(false);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const zip = value.trim();
    if (!/^\d{5}$/.test(zip)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onSubmit(zip);
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-1.5" aria-label="Find clearance near you" data-testid="landing-zip-form">
      <div className="flex flex-col gap-2 sm:flex-row">
        <label className="flex h-[52px] min-w-0 flex-none items-center gap-2 rounded-full sm:flex-1 border px-[18px] focus-within:border-[#8B5CF6] sm:h-[54px]" style={{ borderColor: invalid ? HF.red : "var(--hf-hero-border)", color: MUTED, background: HF.surface }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></svg>
          <input
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={5}
            aria-label="ZIP code"
            placeholder="Enter your ZIP code"
            value={value}
            onChange={(event) => setValue(event.target.value.replace(/\D/g, ""))}
            className="min-w-0 flex-1 border-0 bg-transparent text-[16px] font-semibold outline-none placeholder:font-normal placeholder:text-[var(--hf-placeholder)]"
            style={{ color: HF.ink }}
          />
        </label>
        <button type="submit" className="h-[52px] flex-shrink-0 whitespace-nowrap rounded-full px-[26px] text-[16px] font-bold text-white hover:brightness-125 sm:h-[54px]" style={{ background: PURPLE }}>
          Show deals
        </button>
      </div>
      {invalid && <span className="text-[13px] font-semibold" style={{ color: HF.red }}>Enter a 5-digit ZIP code</span>}
    </form>
  );
}
